import { useLocation, useNavigate } from 'react-router-dom'
import { Box, Typography, Button, Avatar } from '@mui/material'
import SearchOffIcon from '@mui/icons-material/SearchOff'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import HomeIcon from '@mui/icons-material/Home'

interface NotFoundState {
  originalUrl?: string
}

const NotFound = (): JSX.Element => {
  const location = useLocation()
  const navigate = useNavigate()
  const { originalUrl } = (location.state || {}) as NotFoundState

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        px: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2.5,
          p: { xs: 4, sm: 6 },
          borderRadius: 4,
          bgcolor: 'white',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 124, 145, 0.08)',
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
        }}
      >
        <Avatar
          sx={{
            width: 80,
            height: 80,
            background: 'linear-gradient(135deg, #007C91 0%, #00ACC1 100%)',
            boxShadow: '0 4px 16px rgba(0, 124, 145, 0.3)',
          }}
        >
          <SearchOffIcon sx={{ fontSize: 40, color: 'white' }} />
        </Avatar>

        <Box>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: '3.5rem',
              lineHeight: 1,
              background: 'linear-gradient(135deg, #007C91 0%, #00ACC1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
            }}
          >
            404
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#263238', mb: 0.5 }}>
            Page Not Found
          </Typography>
          <Typography variant="body2" sx={{ color: '#78909C', lineHeight: 1.6 }}>
            {originalUrl ? (
              <>
                The page{' '}
                <Box
                  component="code"
                  sx={{
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: '#E0F7FA',
                    color: '#007C91',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                  }}
                >
                  {originalUrl}
                </Box>{' '}
                could not be found.
              </>
            ) : (
              "The page you're looking for doesn't exist or has been moved."
            )}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              borderColor: 'rgba(0, 124, 145, 0.3)',
              color: '#007C91',
              '&:hover': { borderColor: '#007C91', bgcolor: 'rgba(0, 124, 145, 0.04)' },
            }}
          >
            Go Back
          </Button>
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              bgcolor: '#007C91',
              boxShadow: '0 2px 8px rgba(0, 124, 145, 0.3)',
              '&:hover': { bgcolor: '#006B7D' },
            }}
          >
            Home
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default NotFound
